import type { ComponentProps, ReactNode } from 'react';
import { cn } from './cn';
import { focusRing } from './focus';
import { Kbd } from './Kbd';

/**
 * A row of a list or of search results (sections, palette, quick create): icon, label, hint and shortcut. Its role
 * (`option`, `menuitem`, none for a plain button) comes from the list that holds it; `selected` sets `aria-selected`.
 */
export function ListItem({
  icon,
  label,
  hint,
  shortcut,
  selected,
  danger,
  size = 'md',
  className,
  type = 'button',
  ...rest
}: Omit<ComponentProps<'button'>, 'children'> & {
  icon?: ReactNode;
  label: ReactNode;
  hint?: ReactNode;
  shortcut?: string;
  selected?: boolean;
  danger?: boolean;
  /** `lg`: a finger-sized 48 px row (the phone's full-screen menu). */
  size?: 'md' | 'lg';
}) {
  return (
    <button
      type={type}
      aria-selected={rest.role === 'option' ? !!selected : undefined}
      data-selected={selected || undefined}
      className={cn(
        'flex h-9 w-full items-center gap-2 rounded-xl px-2.5 text-left font-sans text-sm text-fg transition-colors duration-(--duration-fast) hover:bg-surface-2 data-[selected]:bg-surface-2 disabled:pointer-events-none disabled:opacity-50 pointer-coarse:h-11',
        size === 'lg' && 'h-12 gap-3 rounded-2xl px-3 text-base pointer-coarse:h-12',
        danger && 'text-danger',
        focusRing,
        className,
      )}
      {...rest}
    >
      {icon && <span className="flex h-4 w-4 shrink-0 items-center justify-center text-fg-muted">{icon}</span>}
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {hint && <span className="shrink-0 text-xs text-fg-muted">{hint}</span>}
      {shortcut && <Kbd>{shortcut}</Kbd>}
    </button>
  );
}
